/**
 * 统一时间线数据 Hook
 * 对应板块: 板块 C、D、E、F — 趋势图数据
 *
 * 用于板块 C、D、E、F 的统一数据获取和转换，通过 metric 参数区分不同指标类型。
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getAdCreativeTimeline } from '@op/services/adCreative';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ, DBO_U } from '@op/utils/format';
import type {
  TRequestBackType,
  TRequestPieBackType,
} from '@op/utils/requestUtil';
import type { ParamsTimeType } from '@op/types/common';

/** 标记 series 是否来自 compare_timeline（交叉分析 / 对比周期） */
type TSeriesWithCompareFlag = {
  name: string;
  data: number[];
  isCompare?: boolean;
};

// Timeline metric 枚举
export type TimelineMetric =
  | 'creatives_trend' // 板块 C：趋势图
  | 'creative_count_stat' // 板块 D-1：渠道在投广告数
  | 'platform_percent_stat' // 板块 D-1：渠道占比
  | 'creative_score_stat' // 板块 D-2：热度估值
  | 'impression_stat' // 板块 D-2：累计曝光量
  | 'country_stat' // 板块 E：国家分析
  | 'creative_type_stat' // 板块 F-1：素材类型
  | 'engagement_stat'; // 板块 F-2：互动类型

// Y轴格式类型
export type YAxisFormat = 'integer' | 'percent' | 'largeNumber';

// 游戏 ID 信息
export interface IGameInfo {
  edition_unified_id: string;
  id_type: 'unified_id' | 'edition_id';
  entity_type: 'pc' | 'mobile' | 'console';
  release_time?: string;
}

// 下载额外数据参数
export interface IDownloadExtraData {
  series?: Array<{ name: string; data: number[] }>;
  legend?: string[];
  tableRecord?: Array<any>;
  /** 过滤后的主数据 series */
  filteredSeries?: Array<{ name: string; data: number[] }>;
  /** 过滤后的主数据 xAxis */
  filteredXAxis?: string[];
}

// transformTableData 上下文
export interface ITransformTableDataContext {
  chartData: TRequestBackType;
  extraData?: IDownloadExtraData;
}

/** 调用 download() 时传入，用于与表格展示一致的列与交叉/过滤数据 */
export interface IDownloadInvokeOptions {
  extraData?: IDownloadExtraData;
  /** 数据列 key 顺序（不含 time）；不传则用 chartData.legend */
  legend?: string[];
  /** 表头覆盖：dimension key -> 展示文案（下载配置 AdsOverviewTimeline 内合并） */
  columnHeaders?: Record<string, string>;
  /** 数值列 Excel numFmt，如占比下载用 '0.00%' */
  valueNumFmt?: string;
}

// 下载配置
export interface IDownloadConfig {
  /** 下载 key，用于 DBO_U.download，默认 'AdsOverviewTimeline' */
  key?: string;
  /** 下载文件名前缀，默认使用 metric */
  fileName?: string;
  /** 下载前对 tableRecord 数据进行预处理，接收原始数据和上下文信息 */
  transformTableData?: (
    tableRecord: Array<any>,
    context: ITransformTableDataContext,
  ) => Array<any>;
}

// Hook 参数
export interface UseTimelineDataOptions {
  /** 时间范围筛选器 */
  filter?: {
    start_time: string;
    end_time: string;
    date_type?: 'daily' | 'weekly' | 'monthly';
  };
  /** 游戏 ID 信息 */
  gameInfo?: IGameInfo;
  /** 指标类型 */
  metric: TimelineMetric;
  /** 指定维度值列表，留空则自动取 TopN */
  dimensionValues?: string[];
  /** Top N 数量，默认 10，最大 100 */
  topN?: number;
  /** 是否启用请求，默认 true */
  enabled?: boolean;
  /** 下载配置，传入后 hook 将提供 download 方法 */
  downloadConfig?: IDownloadConfig;
  /** 静态附加数据，会与 download() 入参 extraData 合并后传给 transformTableData */
  downloadExtraData?: IDownloadExtraData;
  /** 是否需要交叉对比时间线数据，默认 false */
  needCompareTimeline?: boolean; 
}

// API 响应结构
export interface IApiTimelineResponse {
  timeline: Array<{
    time: string;
    values: Array<{
      dimension_value: string;
      dimension_value_zh?: string; // 仅 metric=creative_type_stat 时返回
      value: number;
    }>;
  }>;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  /** 最新时间快照（用于饼图展示），取时间线最后一个时间点的 values */
  latest_snapshot?: Array<{
    dimension_value: string;
    dimension_value_zh?: string;
    value: number;
  }>;
  compare_timeline?: Array<{
    time: string;
    values: Array<{
      dimension_value: string;
      value: number;
    }>;
  }>;
  compare_timeline_of_start_time?: string;
  compare_timeline_of_end_time?: string;
}

// 维度翻译映射
export interface IDimensionMap {
  [key: string]: {
    en: string; // 英文名称（dimension_value）
    zh: string; // 中文名称（dimension_value_zh，若无则使用 en）
  };
}

// Hook 返回值
export interface IUseTimelineDataResult {
  /** 转换后的图表数据（TRequestBackType 格式） */
  chartData: TRequestBackType;
  /** 转换后的饼图数据（TRequestPieBackType 格式），基于 latest_snapshot 转换 */
  pieData: TRequestPieBackType;
  /** 对比周期图表数据（needCompareTimeline 为 true 时返回） */
  compareChartData?: TRequestBackType;
  /** 交叉分析图表数据（needCompareTimeline 为 true 时返回） */
  crossAnalysisData?: TRequestBackType;
  /** 原始 API 响应数据 */
  rawData: IApiTimelineResponse | null;
  /** 加载状态 */
  loading: boolean;
  /** 是否为空数据 */
  isEmpty: boolean;
  /** 维度列表（去重后的 dimension_value 列表） */
  dimensions: string[];
  /** 维度翻译映射（dimension_value -> { en, zh }） */
  dimensionMap: IDimensionMap;
  /** 下载方法（需传入 downloadConfig 才可用）；可传入与表格一致的列、extraData */
  download: ((options?: IDownloadInvokeOptions) => void) | null;
}

// 空数据默认值
const EMPTY_CHART_DATA: TRequestBackType = {
  series: [],
  xAxis: [],
  legend: [],
  isEmpty: true,
};
const EMPTY_PIE_DATA: TRequestPieBackType = {
  series: [],
  legend: [],
  isEmpty: true,
};

/**
 * 将 API 返回的 timeline 数据转换为 TRequestBackType 格式
 *
 * 数据格式说明：
 * - API 返回: [{ time: '2026-03-28', values: [{ dimension_value: 'youtube', value: 100 }, ...] }]
 * - 转换后: { xAxis: ['2026-03-28', ...], series: [{ name: 'youtube', data: [100, ...] }], ... }
 */
function transformTimelineToChartData(
  timeline: IApiTimelineResponse['timeline'],
  options: {
    date_type: string;
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    needTable?: boolean; 
  },
): TRequestBackType {
  if (!timeline?.length) {
    return { series: [], xAxis: [], legend: [], isEmpty: true };
  }

  // 将 values 数组展开为长格式数据
  const longFormatData = timeline.flatMap(item =>
    (item.values || []).map(v => ({
      time: item.time,
      dimension_value: v.dimension_value,
      value: v.value ?? 0,
    })),
  );

  // 使用 DBO_REQ.listKeys 转换为标准图表数据格式
  const chartData = DBO_REQ.listKeys(
    {
      timeline: longFormatData,
      timeKey: 'time',
      valueKey: 'value',
      key: 'dimension_value',
    },
    {
      timeline_of_start_time: options.timeline_of_start_time,
      timeline_of_end_time: options.timeline_of_end_time,
      filter: { date_type: options.date_type as ParamsTimeType },
      needTable: options.needTable || false,
      moreLineTable: true,
      fillTableDates: true,
    },
  );

  return chartData;
}

/**
 * 将 API 返回的 latest_snapshot 转换为 TRequestPieBackType 格式
 *
 * 数据格式说明：
 * - API 返回: [{ dimension_value: 'youtube', value: 100 }, { dimension_value: 'tiktok', value: 80 }]
 * - 转换后: { series: [{ name: 'youtube', value: 100 }, { name: 'tiktok', value: 80 }], legend: ['youtube', 'tiktok'], totalSeries: 180 }
 */
function transformSnapshotToPieData(
  snapshot: IApiTimelineResponse['latest_snapshot'],
): TRequestPieBackType {
  if (!snapshot?.length) {
    return { series: [], legend: [], isEmpty: true };
  }

  const series = snapshot.map(item => ({
    name: item.dimension_value,
    value: item.value ?? 0,
  }));

  const totalSeries = series.reduce((sum, item) => sum + item.value, 0);

  return {
    series,
    legend: series.map(item => item.name),
    totalSeries,
    isEmpty: false,
  };
}

/**
 * 提取所有维度值（去重）
 */
function extractDimensions(
  timeline: IApiTimelineResponse['timeline'],
): string[] {
  const dimensionSet = new Set<string>();

  timeline?.forEach(item => {
    item.values?.forEach(v => {
      if (v.dimension_value) {
        dimensionSet.add(v.dimension_value);
      }
    });
  });

  return Array.from(dimensionSet);
}

/**
 * 提取维度翻译映射（dimension_value -> { en, zh }）
 */
function extractDimensionMap(
  timeline: IApiTimelineResponse['timeline'],
): IDimensionMap {
  const map: IDimensionMap = {};
  timeline?.forEach(item => {
    item.values?.forEach(v => {
      if (v.dimension_value && !map[v.dimension_value]) {
        map[v.dimension_value] = {
          en: v.dimension_value,
          zh: v.dimension_value_zh || v.dimension_value,
        };
      }
    });
  });
  return map;
}

export const useTimelineData = (
  options: UseTimelineDataOptions,
): IUseTimelineDataResult => {
  const {
    filter,
    gameInfo,
    metric,
    dimensionValues,
    topN = 10,
    enabled = true,
    downloadConfig,
    downloadExtraData: optionDownloadExtraData,
    needCompareTimeline = false, 
  } = options;

  // ==================== 数据状态 ====================
  // 主数据 + compare_timeline 的 series 合并存放在 chartData.series 中，
  // compare 部分通过 series[i].isCompare === true 标记，避免多份状态导致的首帧渲染错位
  const [chartData, setChartData] =
    useState<TRequestBackType>(EMPTY_CHART_DATA);
  const [pieData, setPieData] = useState<TRequestPieBackType>(EMPTY_PIE_DATA);
  const [rawData, setRawData] = useState<IApiTimelineResponse | null>(null);
  const [dimensions, setDimensions] = useState<string[]>([]);
  const [dimensionMap, setDimensionMap] = useState<IDimensionMap>({});

  const { loading, wrapperCancelRequest } = useCancelRequestHook({
    clear: () => {
      setChartData(EMPTY_CHART_DATA);
      setPieData(EMPTY_PIE_DATA);
      setRawData(null);
      setDimensions([]);
      setDimensionMap({});
    },
  });

  // ==================== 数据请求 + 格式化 ====================
  const fetchData = useCallback(() => {
    if (!filter || !gameInfo || !enabled) return;

    return wrapperCancelRequest(getAdCreativeTimeline, {
      metric,
      dimension_values: dimensionValues,
      top_n: topN,
      need_compare_timeline: needCompareTimeline,
      ...filter,
    }).then((rawRes: unknown) => {
      const res = rawRes as IApiTimelineResponse;
      // 空数据处理
      if (!res?.timeline?.length) {
        setChartData(EMPTY_CHART_DATA);
        setPieData(EMPTY_PIE_DATA);
        setRawData(res);
        setDimensions([]);
        setDimensionMap({});
        return res;
      }

      // 构建转换配置（用于 DBO_REQ.listKeys）
      const date_type = filter?.date_type || 'daily';
      const transformOptions = {
        date_type,
        timeline_of_start_time: res.timeline_of_start_time,
        timeline_of_end_time: res.timeline_of_end_time,
        needTable: true, // 生成 tableRecord 用于表格展示和下载 
      };

      // 主数据
      const primaryChartData = transformTimelineToChartData(
        res.timeline,
        transformOptions,
      );

      // 对比/交叉分析数据：直接合并进主 chartData.series（用 isCompare 标记），
      // 下游只需读取 chartData.series 一个字段，避免首帧 primary/compare 错位
      let mergedChartData: TRequestBackType = primaryChartData;
      if (needCompareTimeline && res.compare_timeline?.length) {
        const compareTransformOptions = {
          date_type,
          timeline_of_start_time:
            res.compare_timeline_of_start_time || res.timeline_of_start_time,
          timeline_of_end_time:
            res.compare_timeline_of_end_time || res.timeline_of_end_time,
        };
        const compareChart = transformTimelineToChartData(
          res.compare_timeline,
          compareTransformOptions,
        );
        const compareSeries: TSeriesWithCompareFlag[] = (
          compareChart.series || []
        ).map(s => ({ ...s, isCompare: true }));
        mergedChartData = {
          ...primaryChartData,
          series: [...(primaryChartData.series || []), ...compareSeries],
        };
      }

      setChartData(mergedChartData);

      // 转换饼图数据（基于 latest_snapshot）
      setPieData(transformSnapshotToPieData(res.latest_snapshot));

      // 原始数据 + 维度列表 + 维度翻译映射
      setRawData(res);
      setDimensions(extractDimensions(res.timeline));
      setDimensionMap(extractDimensionMap(res.timeline));

      return res;
    });
  }, [filter, metric, dimensionValues, topN, enabled, needCompareTimeline]);
  // 注意: 不要包含 wrapperCancelRequest（它是稳定引用）

  // 自动请求
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==================== 派生状态 ====================
  const isEmpty = chartData.isEmpty ?? false;

  // 从合并后的 chartData.series 中提取 compare 部分（用 isCompare 标记），
  // 保持对外 API（compareChartData / crossAnalysisData）不变
  const compareSeries = useMemo(() => {
    return ((chartData.series || []) as TSeriesWithCompareFlag[]).filter(
      s => s.isCompare,
    );
  }, [chartData.series]);

  // compareChartData / crossAnalysisData 当前来自同一份 compare_timeline，
  // 此处导出同一对象，下游若有差异化处理可在外层再过滤
  const compareViewData = useMemo<TRequestBackType | undefined>(() => {
    if (!compareSeries.length) return undefined;
    return {
      ...chartData,
      series: compareSeries,
      legend: compareSeries.map(s => s.name),
      isEmpty: false,
    };
  }, [chartData, compareSeries]);

  // ==================== 下载方法 ====================
  const handleDownload = useCallback((invokeOpts?: IDownloadInvokeOptions) => {
    if (!downloadConfig) return;

    if (
      !chartData ||
      chartData.isEmpty ||
      !chartData.xAxis?.length ||
      !chartData.series?.length
    ) {
      return;
    }

    const mergedExtra: IDownloadExtraData = {
      ...optionDownloadExtraData,
      ...invokeOpts?.extraData,
    };

    const tableRecord = chartData.tableRecord || [];
    let transformedData = tableRecord;

    if (downloadConfig.transformTableData) {
      transformedData = downloadConfig.transformTableData(tableRecord, {
        chartData,
        extraData: mergedExtra,
      });
    }

    DBO_U.download({
      key: downloadConfig.key || 'AdsOverviewTimeline',
      fileName: downloadConfig.fileName || metric,
      filter,
      auxiliaryParams: {
        legend: invokeOpts?.legend ?? chartData.legend,
        columnHeaders: invokeOpts?.columnHeaders,
        dimensionMap,
        metric,
        valueNumFmt: invokeOpts?.valueNumFmt,
      },
      data: transformedData,
    });
  }, [chartData, dimensionMap, optionDownloadExtraData, downloadConfig, metric, filter]);

  const download = downloadConfig ? handleDownload : null;

  return {
    chartData,
    pieData,
    compareChartData: compareViewData,
    crossAnalysisData: compareViewData,
    rawData,
    loading: !!loading,
    isEmpty,
    dimensions,
    dimensionMap,
    download,
  };
};

export default useTimelineData;
